import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-hot-toast";
import { BsShieldFillCheck } from "react-icons/bs";

import { TipoGuia, useGetGuiaRedirecionamento } from "hooks/groupPay/useGetGuiaRedirecionamento";
import { usePostConfigurarBuscaEmail } from "hooks/groupPay/usePostConfigurarBuscaEmail";
import { TestStatusPayload, useGetStatusTesteEmail } from "hooks/groupPay/useGetStatusTesteEmail";
import AlertBanner, { AlertBannerProps } from "shared/components/alertBanner/AlertBanner";
import CustomAlert from "shared/components/customAlert/CustomAlert";
import CustomButton from "shared/components/button/CustomButton";
import { EnumTipoConfiguracaoEmail } from "constants/EnumTipoConfiguracaoEmail";

import { ConfiguracaoEmailResponse } from "types/response/groupPay/ConfiguracaoEmailResponse";
import { ConfigurarEmailUnicoSchema } from "./schemas/ConfigurarEmailUnicoSchema";
import { useStyles } from "./styles/ConfigurarEmailUnicoStyles";
import { Utils } from "./utils/utils";
import FormularioEmailUnico from "./components/FormularioEmailUnico";
import { pertenceAoTesteSolicitado, ReferenciaDoTeste } from "./utils/resultadoDoTesteSolicitado";
import { NotificationProps } from "shared/components/notification/Notification";

interface ConfigurarEmailUnicoProps {
  groupPayEmail: string;
  handleNotification: (result: NotificationProps) => void;
  isInsideWizard?: boolean;
  initialData: ConfiguracaoEmailResponse | null;
  onConfiguracaoSalva?: (isConfigurado: boolean) => void;
}

export interface ConfigurarEmailUnicoForm {
  email: string;
}

/** Depois disso, a validação deixa de ser "aguarde" e passa a oferecer saída ao usuário. */
const LIMITE_VALIDACAO_MINUTOS = 10;

const INTERVALO_POLLING_MS = 60000;

/** Enquanto o resultado do teste recém-disparado não chega, a consulta é repetida neste ritmo. */
const INTERVALO_ESPERA_TESTE_NOVO_MS = 5000;

/** 24 consultas a cada 5 s: dois minutos esperando o mailreader registrar o teste novo. */
const MAX_CONSULTAS_ESPERA_TESTE_NOVO = 24;

const ConfigurarEmailUnico: React.FC<ConfigurarEmailUnicoProps> = ({
  groupPayEmail,
  handleNotification,
  isInsideWizard,
  initialData,
  onConfiguracaoSalva,
}) => {
  const classes = useStyles();
  const [alert, setAlert] = useState<AlertBannerProps>({ show: false });
  const [testFailures, setTestFailures] = useState(0);
  const [isSalvando, setIsSalvando] = useState(false);
  const [statusAtual, setStatusAtual] = useState<string | null>(null);
  const [dataHoraTeste, setDataHoraTeste] = useState<string | null>(null);
  const [showModalRegraConfigurada, setShowModalRegraConfigurada] = useState(false);
  const [formDataToSave, setFormDataToSave] = useState<ConfigurarEmailUnicoForm | null>(null);

  // Teste disparado pelo último salvar e ainda sem resultado próprio. Enquanto houver, status
  // anteriores a ele são descartados — ver pertenceAoTesteSolicitado.
  const referenciaDoTesteRef = useRef<ReferenciaDoTeste | null>(null);
  const consultasEsperandoTesteNovoRef = useRef(0);
  const esperaTesteNovoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `testarRegra` muda de identidade a cada render (os callbacks são recriados no corpo do
  // componente). Mantê-lo nas dependências reiniciava o intervalo antes de ele completar
  // um ciclo, e a consulta automática praticamente nunca acontecia.
  const testarRegraRef = useRef<() => void>(() => undefined);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset: resetForm,
  } = useForm<ConfigurarEmailUnicoForm>({
    resolver: yupResolver(ConfigurarEmailUnicoSchema),
    mode: "onBlur",
    defaultValues: { email: "" },
  });

  const { mutate: salvarConfiguracao } = usePostConfigurarBuscaEmail({
    onSuccess: () => {
      setTestFailures(0);
      onConfiguracaoSalva?.(true);
      testarRegra();
    },
    onError: (error: any) => {
      encerrarEsperaDoTesteNovo();
      const errorMessage = error?.response?.data?.message || "Ocorreu uma falha ao salvar.";
      toast.error(errorMessage);
      setIsSalvando(false);
    },
  });

  const { mutate: getGuia } = useGetGuiaRedirecionamento();

  const { testar: testarRegra, isTesting: isVerificando } = useGetStatusTesteEmail({
    onSuccess: (data) => {
      const payload = data?.payload;
      const status = payload?.status;

      const referencia = referenciaDoTesteRef.current;
      if (referencia && !pertenceAoTesteSolicitado(payload?.dataHoraTeste, referencia)) {
        aguardarResultadoDoTesteNovo();
        return;
      }
      encerrarEsperaDoTesteNovo();

      setStatusAtual(status ?? null);
      setDataHoraTeste(payload?.dataHoraTeste ?? null);
      setIsSalvando(false);
      if (status === "CONCLUIDO") {
        mostrarMensagemSucesso();
      } else if (status === "PENDENTE_DE_VALIDACAO") {
        mostrarMensagemPendenteDeValidacao();
      } else if (status === "PENDENTE") {
        mostrarMensagemPendente(payload);
      } else if (status === "ERRO" || !status) {
        mostrarMensagensErro();
      }
    },
    onError: () => {
      encerrarEsperaDoTesteNovo();
      setIsSalvando(false);
      mostrarMensagensErro();
    },
  });

  const encerrarEsperaDoTesteNovo = () => {
    referenciaDoTesteRef.current = null;
    consultasEsperandoTesteNovoRef.current = 0;
    if (esperaTesteNovoTimeoutRef.current) {
      clearTimeout(esperaTesteNovoTimeoutRef.current);
      esperaTesteNovoTimeoutRef.current = null;
    }
  };

  /**
   * O status recebido ainda é de uma tentativa anterior: o e-mail de teste do último salvar
   * nem saiu. Mostrar esse resultado seria dar um veredito sobre o teste errado — era assim que
   * um ERRO antigo virava "Refaça a regra" no mesmo instante do clique. A tela segue no aviso
   * de espera e consulta de novo em poucos segundos.
   */
  const aguardarResultadoDoTesteNovo = () => {
    consultasEsperandoTesteNovoRef.current += 1;

    if (consultasEsperandoTesteNovoRef.current > MAX_CONSULTAS_ESPERA_TESTE_NOVO) {
      // O teste não chegou a ser registrado. Em vez de exibir o resultado antigo como se fosse
      // do teste novo, a tela diz o que sabe: a validação não respondeu.
      encerrarEsperaDoTesteNovo();
      setIsSalvando(false);
      mostrarValidacaoSemResposta();
      return;
    }

    handleNotification({
      show: true,
      type: "warning",
      message: "Aguarde, estamos validando seu redirecionamento de e-mail",
    });
    esperaTesteNovoTimeoutRef.current = setTimeout(() => {
      testarRegraRef.current();
    }, INTERVALO_ESPERA_TESTE_NOVO_MS);
  };

  const mensagemDeAjuda = () => (
    <>
      Está com dificuldades para configurar o redirecionamento? Acesse nosso{" "}
      <span className={classes.link} onClick={handleSigaOsPassosClick}>
        passo a passo
      </span>{" "}
      ou fale com a nossa{" "}
      <span className={classes.link} onClick={redirectWhatsappSuporte}>
        equipe de suporte
      </span>
      .
    </>
  );

  const mostrarMensagemSucesso = () => {
    setTestFailures(0);
    setAlert({ show: false });
    handleNotification({ show: true, message: "Configuração concluída com sucesso", type: "success" });
  };

  const mostrarValidacaoSemResposta = () => {
    handleNotification({
      show: true,
      type: "warning",
      message:
        "A validação está demorando mais que o esperado. Confira a regra criada no servidor de e-mail e refaça a configuração, se necessário.",
    });
    setAlert({ show: true, message: mensagemDeAjuda() });
  };

  const mostrarMensagemPendenteDeValidacao = () => {
    setTestFailures(0);
    handleNotification({
      show: true,
      message: "Aguardando confirmação da configuração do redirecionamento de e-mail para concluir o processo no sistema.",
      type: "warning"
    });
  };

  const mostrarMensagensErro = () => {
    setTestFailures((prev) => prev + 1);
    handleNotification({
      show: true,
      type: "error",
      message: "Erro no redirecionamento do e-mail",
    });
    setAlert({
      show: true,
      message: (
        <>
          Erro na validação da regra de redirecionamento de e-mail criada.{" "}
          <strong>Refaça a regra para prosseguir</strong>.
        </>
      ),
    });
  };

  /**
   * A espera tem prazo. Dentro da janela, o aviso é de paciência; passado o limite, a tela
   * assume que a confirmação pode não chegar e oferece o caminho de volta — sem isso, o
   * usuário ficava preso no aviso amarelo, com o botão desabilitado e nenhuma ação possível.
   */
  const mostrarMensagemPendente = (payload?: TestStatusPayload | null) => {
    setTestFailures(0);
    const dentroDaJanela = payload ? isTestePendente(payload, LIMITE_VALIDACAO_MINUTOS) : true;

    handleNotification({
      show: true,
      type: "warning",
      message: dentroDaJanela
        ? "Aguarde, estamos validando seu redirecionamento de e-mail"
        : "A validação está demorando mais que o esperado. Confira a regra criada no servidor de e-mail e refaça a configuração, se necessário.",
    });

    setAlert(dentroDaJanela ? { show: false } : { show: true, message: mensagemDeAjuda() });
  };

  const isTestePendente = (data: TestStatusPayload, limiteMinutos: number = 10): boolean => {
    if (data.status !== "PENDENTE") {
      return false;
    }

    if (!data.dataHoraTeste) {
      return true;
    }

    try {
      const dataHoraTeste = new Date(data.dataHoraTeste);
      const agora = new Date();
      
      if (isNaN(dataHoraTeste.getTime())) {
        return true;
      }

      const diferencaMilisegundos = agora.getTime() - dataHoraTeste.getTime();
      const diferencaMinutos = diferencaMilisegundos / 1000 / 60;

      return diferencaMinutos < limiteMinutos;
    } catch (error) {
      return true;
    }
  };

  useEffect(() => {
    if (initialData) {
      resetForm({ email: initialData.emailTesteEnvio });
      testarRegra();
    }
    setTestFailures(0);
  }, [initialData]);

  useEffect(() => {
    if (testFailures >= 3) {
      setAlert({ show: true, message: mensagemDeAjuda() });
    }
  }, [testFailures]);

  useEffect(() => {
    testarRegraRef.current = testarRegra;
  });

  useEffect(() => {
    return () => {
      if (esperaTesteNovoTimeoutRef.current) {
        clearTimeout(esperaTesteNovoTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (statusAtual !== "PENDENTE") {
      return;
    }

    const intervalId = setInterval(() => {
      testarRegraRef.current();
    }, INTERVALO_POLLING_MS);

    return () => clearInterval(intervalId);
  }, [statusAtual]);

  const salvarConfiguracaoDireta = (data: ConfigurarEmailUnicoForm) => {
    setAlert({ show: false });
    setIsSalvando(true);
    encerrarEsperaDoTesteNovo();
    referenciaDoTesteRef.current = { dataHoraTesteAnterior: dataHoraTeste, solicitadoEm: Date.now() };
    salvarConfiguracao({
      tipoConfiguracaoEmail: EnumTipoConfiguracaoEmail.REDIRECIONAMENTO_ALIAS,
      email: groupPayEmail,
      emailTesteEnvio: data.email,
    });
  };

  const onSubmit = (data: ConfigurarEmailUnicoForm) => {
    const isGmail = data.email.toLowerCase().trim().endsWith("@gmail.com");
    const hasExistingConfig = !!initialData;

    if (isGmail || hasExistingConfig) {
      setFormDataToSave(data);
      setShowModalRegraConfigurada(true);
    } else {
      salvarConfiguracaoDireta(data);
    }
  };

  const handleConfirmarRegraConfigurada = () => {
    setShowModalRegraConfigurada(false);
    if (formDataToSave) {
      salvarConfiguracaoDireta(formDataToSave);
      setFormDataToSave(null);
    }
  };

  const handleCancelarRegraConfigurada = () => {
    setShowModalRegraConfigurada(false);
    setFormDataToSave(null);
  };

  const handleSigaOsPassosClick = async () => {
    getGuia({ tipoGuia: TipoGuia.REDIRECIONAMENTO_EMAIL_UNICO });
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(groupPayEmail);
  };

  const redirectWhatsappSuporte = () => {
    Utils.redirectToWhatsApp();
  };

  return (
    <>
      <CustomAlert
        icon={BsShieldFillCheck}
        iconColor="black"
        open={showModalRegraConfigurada}
        onClose={handleCancelarRegraConfigurada}
        height={"50vh"}
        width={"45vw"}
        title="Confirmação"
        message="A regra de redirecionamento de e-mails já foi configurada no servidor de e-mail da administradora?"
        subMessage="Essa confirmação é importante para garantir que o processo de configuração seja executado corretamente."
        messageAlignment="left"
        buttons={
          <>
            <CustomButton
              id="cancelar-regra-configurada"
              label="Não"
              onClick={handleCancelarRegraConfigurada}
              size="lg"
              className="mb-3 me-3"
              style={{ width: 150 }}
              variant="outline-primary"
            />
            <CustomButton
              id="confirmar-regra-configurada"
              label="Sim"
              onClick={handleConfirmarRegraConfigurada}
              size="lg"
              className="mb-3"
              style={{ width: 150 }}
              variant="primary"
            />
          </>
        }
      />

      <div className={classes.container}>
        <FormularioEmailUnico
          classes={classes}
          isVerificando={isVerificando}
          isSalvando={isSalvando}
          groupPayEmail={groupPayEmail}
          control={control}
          register={register}
          setValue={setValue}
          onSigaOsPassosClick={handleSigaOsPassosClick}
          onCopyToClipboard={handleCopyToClipboard}
          onSubmit={handleSubmit(onSubmit)}
          onVerificarAgora={testarRegra}
          statusAtual={statusAtual}
          dataHoraTeste={dataHoraTeste}
          onConfirmacaoIniciada={() => {
            mostrarMensagemPendente();
            setTimeout(() => {
              testarRegra();
            }, 2000);
          }}
        />

        <div className={classes.alertContainer}>
          <AlertBanner show={alert.show} message={alert.message} />
        </div>
      </div>

    </>
  );
};

export default ConfigurarEmailUnico;
